import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './CompaniesPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');
const STATUS_OPTIONS = ['Aktif', 'Pasif'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Sirketler',
    pageDescription: 'Sistem yonetimi icin firma kayitlari.',
    newCompany: 'Yeni firma',
    companiesLoadFailed: 'Sirketler yuklenemedi.',
    companiesLoadFailedDetail: 'Sirketler yuklenirken hata olustu.',
    selectionsLoadFailed: 'Sektor veya bolge listesi yuklenemedi.',
    requiredFields: 'Ad, sektor ve bolge zorunludur.',
    operationFailed: 'Islem basarisiz oldu.',
    companyUpdated: 'Sirket guncellendi.',
    companyCreated: 'Sirket olusturuldu.',
    operationFailedDetail: 'Islem sirasinda hata olustu.',
    deleteConfirm: 'Bu sirketi silmek istediginizden emin misiniz?',
    deleteFailed: 'Silme islemi basarisiz oldu.',
    companyDeleted: 'Sirket silindi.',
    deleteFailedDetail: 'Silme sirasinda hata olustu.',
    company: 'Sirket',
    sector: 'Sektor',
    region: 'Bolge',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz sirket tanimi bulunamadi.',
    edit: 'Duzenle',
    delete: 'Sil',
    editCompanyAria: 'Sirketi duzenle',
    newCompanyAria: 'Yeni sirket olustur',
    editCompanyTitle: 'Sirketi duzenle',
    newCompanyTitle: 'Yeni sirket olustur',
    close: 'Kapat',
    select: 'Seciniz',
    saving: 'Kaydediliyor...',
    update: 'Guncelle',
    save: 'Kaydet',
    cancel: 'Iptal',
    active: 'Aktif',
    passive: 'Pasif'
  },
  'en-US': {
    pageTitle: 'Companies',
    pageDescription: 'Company records for system management.',
    newCompany: 'New company',
    companiesLoadFailed: 'Companies could not be loaded.',
    companiesLoadFailedDetail: 'An error occurred while loading companies.',
    selectionsLoadFailed: 'Sector or region lists could not be loaded.',
    requiredFields: 'Name, sector and region are required.',
    operationFailed: 'Operation failed.',
    companyUpdated: 'Company updated.',
    companyCreated: 'Company created.',
    operationFailedDetail: 'An error occurred during the operation.',
    deleteConfirm: 'Are you sure you want to delete this company?',
    deleteFailed: 'Delete operation failed.',
    companyDeleted: 'Company deleted.',
    deleteFailedDetail: 'An error occurred during delete.',
    company: 'Company',
    sector: 'Sector',
    region: 'Region',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No company definition found yet.',
    edit: 'Edit',
    delete: 'Delete',
    editCompanyAria: 'Edit company',
    newCompanyAria: 'Create new company',
    editCompanyTitle: 'Edit company',
    newCompanyTitle: 'Create new company',
    close: 'Close',
    select: 'Select',
    saving: 'Saving...',
    update: 'Update',
    save: 'Save',
    cancel: 'Cancel',
    active: 'Active',
    passive: 'Passive'
  }
};

const createFormState = () => ({
  id: null,
  name: '',
  sector: '',
  region: '',
  status: 'Aktif'
});

function CompaniesPage() {
  const { language } = useLanguage();
  const text = UI_TEXT[language] || UI_TEXT['tr-TR'];
  const t = (key) => text[key] || UI_TEXT['tr-TR'][key] || key;
  const statusLabel = (value) => (value === 'Aktif' ? t('active') : t('passive'));

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(createFormState());
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [regions, setRegions] = useState([]);

  const isEditMode = Boolean(formState.id);

  const buildHeaders = useCallback(() => {
    const token = sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, { headers: buildHeaders() });
      if (!response.ok) {
        throw new Error(text.companiesLoadFailed);
      }
      setCompanies(await response.json());
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : text.companiesLoadFailedDetail);
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, text.companiesLoadFailed, text.companiesLoadFailedDetail]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const loadSelections = useCallback(async () => {
    try {
      const [sectorsResponse, regionsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/sectors`, { headers: buildHeaders() }),
        fetch(`${API_BASE_URL}/api/regions`, { headers: buildHeaders() })
      ]);
      if (!sectorsResponse.ok || !regionsResponse.ok) {
        throw new Error(text.selectionsLoadFailed);
      }
      setSectors(await sectorsResponse.json());
      setRegions(await regionsResponse.json());
    } catch (caught) {
      console.error(caught);
    }
  }, [buildHeaders, text.selectionsLoadFailed]);

  useEffect(() => {
    loadSelections();
  }, [loadSelections]);

  const resetForm = useCallback(() => {
    setFormState(createFormState());
    setFormError('');
  }, []);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const preparePayload = useMemo(() => () => ({
    name: formState.name.trim(),
    sector: formState.sector.trim(),
    region: formState.region.trim(),
    status: formState.status
  }), [formState.name, formState.region, formState.sector, formState.status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload();
    if (!payload.name || !payload.sector || !payload.region) {
      setFormError(t('requiredFields'));
      setSaving(false);
      return;
    }

    const method = isEditMode ? 'PUT' : 'POST';
    const url = `${API_BASE_URL}/api/companies${isEditMode ? `/${formState.id}` : ''}`;

    try {
      const response = await fetch(url, {
        method,
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        if (body?.code === 'PACKAGE_LIMIT_EXCEEDED' || body?.code === 'PACKAGE_FEATURE_DISABLED') {
          throw new Error(body.message);
        }
        throw new Error(body?.message || t('operationFailed'));
      }
      setSuccess(isEditMode ? t('companyUpdated') : t('companyCreated'));
      closeModal();
      await loadCompanies();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationFailedDetail'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (company) => {
    setFormState({
      id: company.id,
      name: company.name || '',
      sector: company.sector || '',
      region: company.region || '',
      status: company.status || 'Aktif'
    });
    setSuccess('');
    setFormError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (companyId) => {
    const confirmed = window.confirm(t('deleteConfirm'));
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('deleteFailed'));
      }
      setSuccess(t('companyDeleted'));
      if (formState.id === companyId) {
        closeModal();
      }
      await loadCompanies();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('deleteFailedDetail'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card companies-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={(
          <button type="button" className="btn-secondary" onClick={openCreateModal}>
            {t('newCompany')}
          </button>
        )}
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="companies-grid">
        <section className="companies-table-panel">
          <div className="table-wrapper">
            <table className="companies-table">
              <thead>
                <tr>
                  <th>{t('company')}</th>
                  <th>{t('sector')}</th>
                  <th>{t('region')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="table-placeholder">
                      {t('loading')}
                    </td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={5} className="table-placeholder">
                      {listError}
                    </td>
                  </tr>
                ) : companies.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="table-placeholder">
                      {t('noRecords')}
                    </td>
                  </tr>
                ) : (
                  companies.map((company) => (
                    <tr key={company.id || company.name}>
                      <td>{company.name}</td>
                      <td>{company.sector}</td>
                      <td>{company.region}</td>
                      <td>
                        <span className={company.status === 'Aktif' ? 'badge badge-active' : 'badge'}>
                          {statusLabel(company.status)}
                        </span>
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-edit"
                          onClick={() => handleEdit(company)}
                          aria-label={t('edit')}
                          title={t('edit')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-delete"
                          onClick={() => handleDelete(company.id)}
                          aria-label={t('delete')}
                          title={t('delete')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editCompanyTitle') : t('newCompanyTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="name">{t('company')}</label>
              <input
                id="name"
                name="name"
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                placeholder="Elysium Tech"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="sector">{t('sector')}</label>
              <select
                id="sector"
                name="sector"
                value={formState.sector}
                onChange={(event) => setFormState((current) => ({ ...current, sector: event.target.value }))}
                required
              >
                <option value="">{t('select')}</option>
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.name}>
                    {sector.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="region">{t('region')}</label>
              <select
                id="region"
                name="region"
                value={formState.region}
                onChange={(event) => setFormState((current) => ({ ...current, region: event.target.value }))}
                required
              >
                <option value="">{t('select')}</option>
                {regions.map((region) => (
                  <option key={region.id} value={region.name}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="status">{t('status')}</label>
              <select
                id="status"
                name="status"
                value={formState.status}
                onChange={(event) => setFormState((current) => ({ ...current, status: event.target.value }))}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {statusLabel(option)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('saving') : isEditMode ? t('update') : t('save')}
              </button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default CompaniesPage;
